// Product image gallery (snippets/purolabs-gallery.liquid). The slides are a native scroll-snap row, so swiping,
// trackpads and the keyboard work without this. It adds:
// - thumbnails with previous/next chevrons beside them, the thumbnail of the image in view marked (aria-current
//   "true"/"false"), and on phones a dot per image with the current one marked;
// - slides out of view hidden from screen readers and the keyboard until they scroll in;
// - wrap-round on phones (data-loop): a copy of the last slide goes before the first and of the first after the last,
//   so an image peeks in on both sides; landing on a copy jumps to the real one;
// - deferred images: slides after the first have no image source until the shopper uses the gallery (touch, mouse
//   over it, wheel, keyboard, scroll, a thumbnail or an arrow), then a slide either side of the one in view loads;
// - zoom below 990px (data-zoom-script): tapping an image or its magnifier opens the full-screen viewer, whose script
//   (assets/purolabs-gallery-zoom.js) loads on that first tap.
const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

class Gallery extends HTMLElement {
  #observers = [];

  connectedCallback() {
    this.slides = this.querySelector('[data-gallery-slides]');
    if (!this.slides) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.real = [...this.slides.querySelectorAll('[data-index]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.current = 0;

    if (this.hasAttribute('data-loop')) this.#setUpLoop();
    for (const thumb of this.thumbs) thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb)), { signal });
    this.prev?.addEventListener('click', () => this.goTo(this.current - 1), { signal });
    this.next?.addEventListener('click', () => this.goTo(this.current + 1), { signal });
    this.#observeCurrent();
    this.#waitForUse();
    if (this.dataset.zoomScript) this.slides.addEventListener('click', (event) => this.#zoom(event), { signal });
  }

  // A tap on an image (not the feature tiles) or its magnifier, in a slide in view; swipes don't fire a click
  #zoom(event) {
    if (!matchMedia('(max-width: 989px)').matches) return;
    const target = event.target.closest('[data-gallery-zoom], .pl-gallery__image');
    const slide = target?.closest('[data-index]');
    if (!slide || slide.inert) return;
    const button = slide.querySelector('[data-gallery-zoom]');
    import(this.dataset.zoomScript).then(({ openZoom }) => openZoom(this, Number(slide.dataset.index), button));
  }

  // Theme editor: sections are removed and re-added, so nothing may keep running for a detached copy
  disconnectedCallback() {
    this.abortController?.abort();
    this.waiting?.abort();
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
  }

  goTo(index) {
    // Galleries that wrap round on phones (data-loop) wrap with the arrows too
    const count = this.real.length;
    const wrapped = this.hasAttribute('data-loop') ? (index + count) % count : Math.max(0, Math.min(count - 1, index));
    const slide = this.real[wrapped];
    if (!slide) return;
    this.#start();
    this.#load(slide);
    this.slides.scrollTo({ left: this.#centred(slide), behavior: smooth() });
  }

  // The scroll position that puts the slide in the middle of the row (snapping then settles it)
  #centred(slide) {
    const box = this.slides.getBoundingClientRect();
    const target = slide.getBoundingClientRect();
    return this.slides.scrollLeft + target.left + target.width / 2 - (box.left + box.width / 2);
  }

  // The slide mostly in view is the current one; the others are hidden from screen readers and the keyboard
  #observeCurrent() {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const inView = entry.intersectionRatio >= 0.6;
          entry.target.inert = !inView;
          if (inView) entry.target.removeAttribute('aria-hidden');
          else entry.target.setAttribute('aria-hidden', 'true');
          if (inView) {
            this.current = Number(entry.target.dataset.index);
            this.#mark();
          }
        }
      },
      { root: this.slides, threshold: [0, 0.6] }
    );
    for (const slide of this.real) observer.observe(slide);
    this.#observers.push(observer);
  }

  #mark() {
    const loops = this.hasAttribute('data-loop');
    if (this.prev) this.prev.disabled = !loops && this.current <= 0;
    if (this.next) this.next.disabled = !loops && this.current >= this.real.length - 1;
    // Phones: the dot for the image in view; on a first image with feature tiles the dots centre on the photo
    this.querySelectorAll('.pl-gallery__dot').forEach((dot, index) => dot.toggleAttribute('data-current', index === this.current));
    const featured = this.current === 0 ? this.real[0]?.querySelector('.pl-gallery__featured') : null;
    const tiles = featured?.querySelector('.pl-gallery__tiles');
    const shift = tiles ? (tiles.offsetWidth + parseFloat(getComputedStyle(featured).columnGap || '0')) / 2 : 0;
    this.style.setProperty('--pl-dots-shift', `${shift}px`);
    for (const thumb of this.thumbs) thumb.setAttribute('aria-current', String(Number(thumb.dataset.thumb) === this.current));

    // Keep the current thumbnail in view when the strip scrolls (scrolling the strip only, never the page)
    const thumb = this.thumbs[this.current];
    const strip = this.querySelector('[data-gallery-thumbs]');
    if (!thumb || !strip || strip.scrollWidth <= strip.clientWidth) return;
    const box = strip.getBoundingClientRect();
    const target = thumb.getBoundingClientRect();
    if (target.left < box.left) strip.scrollBy({ left: target.left - box.left, behavior: smooth() });
    else if (target.right > box.right) strip.scrollBy({ left: target.right - box.right, behavior: smooth() });
  }

  // Deferred images wait for the first sign the shopper is using the gallery
  #waitForUse() {
    if (!this.slides.querySelector('[data-srcset]')) return;
    this.waiting = new AbortController();
    const options = { signal: this.waiting.signal, passive: true };
    const start = () => this.#start();
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin']) this.addEventListener(type, start, options);
    this.addEventListener('pointerover', (event) => event.pointerType === 'mouse' && start(), options);
    // Scrolling counts too (screen readers, scrollbars), from the next frame on: the wrap-round's own first scroll
    // into position fires a scroll event as well
    requestAnimationFrame(() => requestAnimationFrame(() => this.slides.addEventListener('scroll', start, options)));
  }

  // From now on, slides load as they come within half a row's width of the view: the ones either side of the
  // current slide
  #start() {
    if (!this.waiting || this.waiting.signal.aborted) return;
    this.waiting.abort();
    const loader = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#load(entry.target);
          loader.unobserve(entry.target);
        }
      },
      { root: this.slides, rootMargin: '0px 50%' }
    );
    for (const slide of this.slides.children) if (slide.querySelector('[data-srcset]')) loader.observe(slide);
    this.#observers.push(loader);
  }

  // A slide and its wrap-round copy load together, so jumping from the copy to the real one shows the same image
  #load(slide) {
    const index = slide.dataset.index ?? slide.dataset.copyOf;
    for (const twin of this.slides.querySelectorAll(`[data-index="${index}"], [data-copy-of="${index}"]`)) {
      for (const el of twin.querySelectorAll('[data-srcset]')) {
        el.srcset = el.dataset.srcset;
        el.removeAttribute('data-srcset');
      }
    }
  }

  #setUpLoop() {
    const { slides, real } = this;
    if (real.length < 2 || slides.querySelector('.pl-gallery__slide--clone')) return;
    const copy = (slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('pl-gallery__slide--clone');
      clone.dataset.copyOf = slide.dataset.index;
      delete clone.dataset.index;
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      for (const img of clone.querySelectorAll('img')) {
        img.removeAttribute('fetchpriority');
        img.loading = 'lazy';
      }
      return clone;
    };
    slides.prepend(copy(real.at(-1)));
    slides.append(copy(real[0]));

    const centre = (slide) => (slides.scrollLeft = this.#centred(slide));
    const { signal } = this.abortController;
    const phone = matchMedia('(max-width: 989px)');
    const reset = () => phone.matches && centre(real[0]);
    reset();
    phone.addEventListener('change', reset, { signal });

    const settle = () => {
      if (!phone.matches) return;
      const box = slides.getBoundingClientRect();
      const middle = box.left + box.width / 2;
      const current = [...slides.children].find((slide) => {
        const r = slide.getBoundingClientRect();
        return r.left <= middle && r.right > middle;
      });
      if (current === slides.firstElementChild) centre(real.at(-1));
      else if (current === slides.lastElementChild) centre(real[0]);
    };
    if ('onscrollend' in window) {
      slides.addEventListener('scrollend', settle, { signal });
    } else {
      let timer;
      slides.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(settle, 150);
        },
        { signal }
      );
    }
  }
}

if (!customElements.get('pl-gallery')) customElements.define('pl-gallery', Gallery);
